/**
 * Product Card Core - Shared utilities for all card versions
 * ES6 Module - import in V1/V2/V3 specific files
 */
import { log } from './np-logger.js';

'use strict';

// Global money format
const moneyFormat = (typeof Shopify !== 'undefined' && Shopify.money_format)
    ? Shopify.money_format : '${{amount}}';

/**
 * Format money from cents to currency string
 * @param {number|string} cents - Amount in cents
 * @param {string} format - Money format string
 * @returns {string} Formatted money string
 */
export function formatMoney(cents, format) {
    if (typeof cents == "string") {
        cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = (format || moneyFormat || '${{amount}}');

    function defaultOption(opt, def) {
        return typeof opt == "undefined" ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ",");
        decimal = defaultOption(decimal, ".");
        if (isNaN(number) || number == null) {
            return 0;
        }
        number = (number / 100.0).toFixed(precision);
        var parts = number.split("."),
            dollars = parts[0].replace(
                /(\d)(?=(\d\d\d)+(?!\d))/g,
                "$1" + thousands
            ),
            cents = parts[1] ? decimal + parts[1] : "";
        return dollars + cents;
    }

    // Check if format string matches expected pattern
    var match = formatString.match(placeholderRegex);
    if (!match) {
        // Fallback to simple formatting (silently, no error)
        return '$' + formatWithDelimiters(cents, 2);
    }

    switch (match[1]) {
        case "amount":
            value = formatWithDelimiters(cents, 2);
            break;
        case "amount_no_decimals":
            value = formatWithDelimiters(cents, 0);
            break;
        case "amount_with_comma_separator":
            value = formatWithDelimiters(cents, 2, ".", ",");
            break;
        case "amount_no_decimals_with_comma_separator":
            value = formatWithDelimiters(cents, 0, ".", ",");
            break;
    }
    var result = formatString.replace(placeholderRegex, value);
    // Remove space between amount and trailing currency symbol (e.g., "100.00 ฿" -> "100.00฿")
    result = result.replace(/(\d)\s+([^\d\s])$/, '$1$2');
    return result;
}

/**
 * Get tier config from element or page global
 * @param {jQuery} $el - Element to check for data-tier-config
 * @returns {string|null} - Tier config ID or null
 */
export function getTierConfig($el) {
    return $el.attr('data-tier-config') || window.TIERED_CART_ACTIVE_CONFIG || null;
}

/**
 * Build cart properties for BF products/bundles
 * @param {string} type - 'product' or 'bundle'
 * @param {string|null} tierConfig - Tier config ID
 * @returns {object} - Properties object for cart
 */
export function buildBfProperties(type, tierConfig, $el) {
    const properties = { _bf_product: 'true' };
    if (tierConfig) {
        properties._tier_config = tierConfig;
    }
    const packs = $el && $el.attr('data-packs');
    if (packs && parseInt(packs) > 0) {
        properties._packs = packs;
    }
    return properties;
}

/**
 * Update cart bubble count from server
 */
export function updateCartBubbleFromServer() {
    jQuery.getJSON('/cart.js').done(function(cart) {
        updateCartBubble(cart.item_count || 0);
    }).fail(function() {
        console.warn('Unable to fetch cart.js to update badge.');
    });
}

/**
 * Update cart bubble display with count
 * @param {number} count - Item count
 */
export function updateCartBubble(count) {
    const $icon = jQuery('#cart-icon-bubble');

    if (!$icon.length) return;

    let $bubble = $icon.find('.cart-count-bubble');

    if (!$bubble.length) {
        $bubble = jQuery(`
        <div class="cart-count-bubble">
            <span aria-hidden="true"></span>
            <span class="visually-hidden"></span>
        </div>
        `);
        $icon.append($bubble);
    }

    $bubble.find('span[aria-hidden="true"]').text(count);
    $bubble.find('.visually-hidden').text(count + (count === 1 ? " item" : " items"));

    if (count > 0) {
        $bubble.show();
        $bubble.addClass('cart-bubble-update');
        setTimeout(() => $bubble.removeClass('cart-bubble-update'), 600);
    } else {
        $bubble.hide();
    }
}

/**
 * Add products to cart
 * @param {Array|Object} products - Product(s) to add
 * @param {boolean} redirectToCheckout - Whether to redirect after adding
 * @param {jQuery} parentEl - Parent element for UI updates
 * @param {jQuery} targetEl - Button element
 * @param {string} buttonDefaultText - Default button text to restore
 * @returns {Promise<Object>} Cart response data
 */
export async function addToCart(products, redirectToCheckout = false, parentEl, targetEl, buttonDefaultText) {
    targetEl.html('Adding...');
    targetEl.css({
        "cursor": "unset",
        "pointer-events": "none",
        "opacity": "0.7"
    });
    parentEl.find('.added-to-cart-message').addClass('bf-2025--hidden');

    try {
        const items = Array.isArray(products) ? products : [products];
        const payload = {
            items: items.map(item => {
                let data = {
                    id: item.variantId,
                    quantity: item.quantity || 1
                };
                if (item.planId) {
                    data.selling_plan = item.planId;
                }
                if (item.properties) {
                    data.properties = item.properties;
                }
                return data;
            })
        };

        const response = await fetch(`/cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        console.log("Added to cart:", data);

        // Trigger iCart slideout to open
        if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
            window.prepareDataForDisplayWidgetByRule();
        }

        if (redirectToCheckout) {
            window.location.href = '/checkout';
        }

        parentEl.find('.added-to-cart-message').removeClass('bf-2025--hidden');
        setTimeout(() => {
            parentEl.find('.added-to-cart-message').addClass('bf-2025--hidden');
        }, 5000);
        parentEl.find('.checkout-btn').removeClass('bf-2025--disabled');

        updateCartBubbleFromServer();
        return data;
    } catch (err) {
        console.error("Error adding to cart:", err);
    } finally {
        targetEl.html(buttonDefaultText);
        targetEl.css({
            "cursor": "pointer",
            "pointer-events": "all",
            "opacity": "1"
        });
    }
}

/**
 * Apply BF Pack Pricing Currency Conversion
 * @param {jQuery} $card - Card element with BF pricing data attributes
 */
export function applyBFPackPricing($card) {
    // Skip if already converted
    if ($card.attr('data-bf-converted') === 'true') {
        console.log('[PRODUCT-CARD] BF Pricing - Already converted, skipping');
        return;
    }

    const bfPackUSD = {
        pack1: parseInt($card.attr('data-bf-pack-1-usd')) || 0,
        pack2: parseInt($card.attr('data-bf-pack-2-usd')) || 0,
        pack3: parseInt($card.attr('data-bf-pack-3-usd')) || 0,
        pack4: parseInt($card.attr('data-bf-pack-4-usd')) || 0
    };

    // Check if BF pack pricing is enabled
    if (!bfPackUSD.pack1) return;

    // compare_at values from Liquid are ALREADY in local currency (Shopify Markets handles conversion)
    const compareAtLocal = {
        pack1: parseInt($card.attr('data-compare-at-pack-1')) || 0,
        pack2: parseInt($card.attr('data-compare-at-pack-2')) || 0,
        pack3: parseInt($card.attr('data-compare-at-pack-3')) || 0,
        pack4: parseInt($card.attr('data-compare-at-pack-4')) || 0
    };

    // Use Shopify's currency rate ONLY for bf_pack_*_usd (which are hardcoded USD cents)
    const currencyRate = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate) ? Shopify.currency.rate : 1.0;
    const cardMoneyFormat = $card.data('money-format') || moneyFormat || '${{amount}}';

    // Convert BF prices from USD to local currency, but keep compareAt as-is (already local)
    const converted = {
        pack1: { price: Math.round(bfPackUSD.pack1 * currencyRate), compareAt: compareAtLocal.pack1 },
        pack2: { price: Math.round(bfPackUSD.pack2 * currencyRate), compareAt: compareAtLocal.pack2 },
        pack3: { price: Math.round(bfPackUSD.pack3 * currencyRate), compareAt: compareAtLocal.pack3 },
        pack4: { price: Math.round(bfPackUSD.pack4 * currencyRate), compareAt: compareAtLocal.pack4 }
    };

    // Store converted values back to data attributes
    $card.attr('data-bf-pack-1', converted.pack1.price);
    $card.attr('data-bf-pack-2', converted.pack2.price);
    $card.attr('data-bf-pack-3', converted.pack3.price);
    $card.attr('data-bf-pack-4', converted.pack4.price);
    $card.attr('data-compare-at-pack-1', converted.pack1.compareAt);
    $card.attr('data-compare-at-pack-2', converted.pack2.compareAt);
    $card.attr('data-compare-at-pack-3', converted.pack3.compareAt);
    $card.attr('data-compare-at-pack-4', converted.pack4.compareAt);

    // Mark as converted
    $card.attr('data-bf-converted', 'true');

    console.log('[PRODUCT-CARD] BF Pack Pricing - Currency Rate:', currencyRate, 'Converted:', converted);

    // Detect card version and update accordingly
    const isV2Card = $card.hasClass('product-card-v2');
    const isV3Card = $card.hasClass('product-card-v3');

    if (isV3Card) {
        // V3 Card: Update pack option displays
        updateV3BFPricing($card, converted, cardMoneyFormat);
    } else if (isV2Card) {
        // V2 Card: Update all variant buttons
        updateV2BFPricing($card, converted, cardMoneyFormat);
    } else {
        // V1 Card: Default to pack 1
        updateV1BFPricing($card, converted, cardMoneyFormat);
    }
}

/**
 * Update V1 card BF pricing displays
 */
function updateV1BFPricing($card, converted, cardMoneyFormat) {
    const packData = converted.pack1;

    if (packData.price > 0) {
        const formattedPrice = formatMoney(packData.price, cardMoneyFormat);
        $card.find('.price-details .discounted-price').text(formattedPrice);
        $card.attr('data-discounted-price', formattedPrice);
        $card.attr('data-discounted-price-no-currency', packData.price);

        if (packData.compareAt > 0) {
            const percentOff = Math.round(((packData.compareAt - packData.price) / packData.compareAt) * 100);
            const saveAmount = formatMoney(packData.compareAt - packData.price, cardMoneyFormat);

            $card.find('.percent-off-free-gift .percent-off').text(percentOff + '% OFF');
            $card.find('.price-off span').text('Save ' + saveAmount);
        }
    }
}

/**
 * Update V2 card BF pricing displays
 */
function updateV2BFPricing($card, converted, cardMoneyFormat) {
    let activePackNum = 3;
    $card.find('.pack-variants li').each(function() {
        const $variantLi = jQuery(this);
        const variantTitle = $variantLi.find('.variant-title').text().toLowerCase();

        let packNum = 1;
        if (variantTitle.includes('4')) packNum = 4;
        else if (variantTitle.includes('3')) packNum = 3;
        else if (variantTitle.includes('2')) packNum = 2;
        else if (variantTitle.includes('1')) packNum = 1;

        if ($variantLi.hasClass('active')) activePackNum = packNum;

        const packData = converted[`pack${packNum}`];
        if (packData.price > 0) {
            $variantLi.attr('data-price', packData.price);
            $variantLi.attr('data-compare-price', packData.compareAt);
            $variantLi.find('.variant-discounted-price').text(formatMoney(packData.price, cardMoneyFormat));

            if (packData.compareAt > 0) {
                const variantPercentOff = Math.round(((packData.compareAt - packData.price) / packData.compareAt) * 100);
                $variantLi.find('.variant-percentage-off-price').text('-' + variantPercentOff + '%');
            }
        }
    });

    const activeData = converted[`pack${activePackNum}`];
    if (activeData.price > 0 && activeData.compareAt > 0) {
        const percentOff = Math.round(((activeData.compareAt - activeData.price) / activeData.compareAt) * 100);
        const saveAmount = formatMoney(activeData.compareAt - activeData.price, cardMoneyFormat);

        $card.find('.percent-off-free-gift .percent-off').text(percentOff + '% off');
        $card.find('.price-off span').text('Save up to ' + saveAmount);
    }
}

/**
 * Update V3 card BF pricing displays
 */
function updateV3BFPricing($card, converted, cardMoneyFormat) {
    // Update pack options with converted prices
    $card.find('.pack-option').each(function() {
        const $option = jQuery(this);
        const packNum = parseInt($option.attr('data-pack-number')) || 1;
        const packData = converted[`pack${packNum}`];

        if (packData && packData.price > 0) {
            $option.attr('data-price', packData.price);
            $option.attr('data-compare-at-price', packData.compareAt);

            // Update per-pack price display
            const pricePerPack = Math.round(packData.price / packNum);
            $option.find('.pack-price').text(formatMoney(pricePerPack, cardMoneyFormat));
        }
    });

    // Trigger V3 price recalculation for active pack
    if (typeof window.ProductCardV3 !== 'undefined' && window.ProductCardV3.updateAllDisplays) {
        window.ProductCardV3.updateAllDisplays($card);
    }
}

/**
 * Calculate percentage off
 * @param {number} compareAt - Compare at price in cents
 * @param {number} price - Current price in cents
 * @returns {number} Percentage off (0-100)
 */
export function calculatePercentOff(compareAt, price) {
    if (compareAt <= 0 || price <= 0) return 0;
    return Math.round(((compareAt - price) / compareAt) * 100);
}

/**
 * Calculate savings amount
 * @param {number} compareAt - Compare at price in cents
 * @param {number} price - Current price in cents
 * @returns {number} Savings in cents
 */
export function calculateSavings(compareAt, price) {
    if (compareAt <= 0) return 0;
    return compareAt - price;
}

/**
 * Get currency rate for conversions
 * @returns {number} Currency rate (1.0 for USD)
 */
export function getCurrencyRate() {
    return (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate)
        ? Shopify.currency.rate
        : 1.0;
}

/**
 * Get money format string
 * @param {jQuery} $el - Optional element to check for data-money-format
 * @returns {string} Money format string
 */
export function getMoneyFormat($el) {
    if ($el && $el.data) {
        return $el.data('money-format') || moneyFormat;
    }
    return moneyFormat;
}

// Backwards-compatible window assignments (for V1/V2 until refactored)
window.ProductCardCore = {
    formatMoney,
    addToCart,
    updateCartBubbleFromServer,
    updateCartBubble,
    getTierConfig,
    buildBfProperties,
    applyBFPackPricing,
    calculatePercentOff,
    calculateSavings,
    getCurrencyRate,
    getMoneyFormat
};

log('[PRODUCT-CARD] Core module loaded');
